import { Button } from "components/ui/button";
import { QuizOption } from "./form-fields/quiz-option";
import Results from "./results";
import { ChevronLeft } from "components/common/icons";
import Simulator from "../common/simulator";
import Disclaimer from "../common/disclaimer";
import useQuiz from "hooks/useQuiz";
import { cn } from "lib/utils";
import { getGTMProps } from "types/common";
import styles from "./simulator-form.module.css";

interface SimulatorFormProps {
  questions: {
    questionType: string;
    questionId: string;
    options: string[];
    maxNumOfAnswer: number;
    displayConfiguration: {
      type: string;
      title: string;
      name: string;
      isRequired?: boolean;
      choices: {
        number: number;
        value: string;
        icon: string;
        score: number;
      }[];
    };
  }[];
}

export function SimulatorForm({ questions: quizSteps }: SimulatorFormProps) {
  const gtmProps = getGTMProps("education");

  const {
    formStep,
    quizCurrentStep,
    answers,
    currentStepData,
    currentAnswer,
    isLastStep,
    handleOptionSelect,
    handleNext,
    handleBack,
  } = useQuiz(quizSteps, gtmProps);

  const choices = currentStepData.displayConfiguration.choices;

  const isRequired = currentStepData.displayConfiguration.isRequired ?? true;
  const totalSteps = quizSteps.length + 1;

  return (
    <Simulator>
      <div className={styles.container}>
        <div
          className={cn(
            styles.innerContainer,
            formStep === 1 && quizCurrentStep !== 4 && styles.step1Container,
            formStep === 1 && quizCurrentStep === 4 && styles.step4Container,
          )}
        >
          <div className={styles.textCenter}>
            <div className={styles.stepText}>
              Step {formStep === 1 ? quizCurrentStep : quizSteps.length + 1} of{" "}
              {totalSteps}
            </div>
            {formStep === 1 && (
              <div>
                <h2 className={styles.titleText}>
                  {currentStepData.displayConfiguration.title}
                </h2>
              </div>
            )}
          </div>

          {formStep === 1 && (
            <>
              <div
                className={cn(
                  styles.gridContainer,
                  choices.length > 4 && styles.gridContainerFlex,
                )}
              >
                {choices.map((option, index) => (
                  <QuizOption
                    key={index}
                    icon={option.icon}
                    title={option.value}
                    isSelected={currentAnswer?.answers.some(
                      (answer) => answer.number === option.number,
                    )}
                    onClick={() => handleOptionSelect(option, !isRequired)}
                  />
                ))}
              </div>

              <div className={styles.buttonContainer}>
                {quizCurrentStep > 1 && (
                  <Button
                    variant={"secondary"}
                    size={"sm"}
                    className={styles.buttonBack}
                    onClick={handleBack}
                  >
                    <ChevronLeft />
                    Back
                  </Button>
                )}
                <Button
                  onClick={handleNext}
                  disabled={isRequired && !currentAnswer?.answers.length}
                >
                  {isLastStep ? "View Results" : "Next"}
                </Button>
              </div>
            </>
          )}

          {formStep === 2 && <Results quizAnswers={answers} />}

          <div>
            <Disclaimer page="education" />
          </div>
        </div>
      </div>
    </Simulator>
  );
}
